
import React, { Component } from "react";
import { Form, FormRenderProps } from "react-final-form";
import { AxiosResponse } from "axios"
import { action, computed, observable, runInAction } from "mobx";
import { observer } from "mobx-react";
import { FieldState, FormApi } from "final-form";

import { Flex } from "@instructure/ui-flex";
import { Heading } from "@instructure/ui-heading";
import { IconSaveLine } from "@instructure/ui-icons";
import { Text } from "@instructure/ui-text";
import { TextInput } from "@instructure/ui-text-input";
import { View } from "@instructure/ui-view";

import { AlertManager, DeclAlert, env, LtiFitHelper, PositionedWaterMark } from "@inst_proserv/toolkit"
import { Field, FormSubmitButton, transformFormErrors, helpers } from "@inst_proserv/finalform-instui"

import IasAppSpinner from "../../components/AppSpinner/AppSpinner";
import { EditableList } from "./components/EditableList";
import { render_contact_item, render_email_edit, render_number_edit } from "./components/ListItems";
import { AvatarEditHeader } from "./components/AvatarEdit";
import { API } from "../../api";

const SectionHeader = (props: { title: string }) => {
    return <Heading level="h2" color="primary" margin="0 0 small">
        <Text weight="light" size="small" transform="uppercase">{props.title}</Text>
    </Heading>
}

const password_validator = (value: string, allValues, meta: FieldState<any>) => {
    if (!value) return;
    if (value.length < 8) {
        return "Must be at least 8 characters"
    }
}

const confirm_password_validator = (value, allValues, meta: FieldState<any>) => {
    if (value != allValues.password) return "Passwords do not match"
}

@observer
export class ProfileEdit extends Component {
    @computed
    get savedState() {
        const data = this.data;
        if (!data) return {}

        const pfl = data.profile;
        const comm_chans: any[] = data.communicationChannels;

        return {
            full_name: pfl.name,
            display_name: pfl.short_name,
            sortable_name: pfl.sortable_name,
            bio: pfl.bio,
            links: [],
            emails: comm_chans.filter(cc => cc.type == "email"),
            sms_numbers: comm_chans.filter(cc => cc.type == "sms"),
            avatar: pfl.avatar_url,
        }
    }

    @observable data;
    @observable loaded = false;
    async fetch_data() {
        try {
            const response = await API.get(`users/${env.userId}/profile`);
            runInAction(() => {
                this.data = response.data;
                this.loaded = true;
            })
        } catch (ex) {
            runInAction(() => {
                this.loaded = true;
            })
        }
    }

    componentDidMount() {
        this.fetch_data();
    }

    @action.bound
    async saveProfile(values, form: FormApi) {
        const parameters = {
            profile: {
                ...this.data.profile,
                name: values.full_name,
                short_name: values.display_name,
                sortable_name: values.sortable_name,
                // bio:
            },
            communicationChannels: [
                ...values.emails.map(cc => ({
                    type: "email",
                    ...cc,
                })),
                ...values.sms_numbers.map(cc => ({
                    type: "sms",
                    ...cc,
                })),
            ],
            password: values.password || null,
        }

        let response: AxiosResponse;
        try {
            response = await API.request({
                url: `users/${env.userId}/profile`,
                method: "POST",
                data: helpers.serializeFormData(parameters),
            })

            if (values.avatar && helpers.asFileUpload(values.avatar)) {
                await API.request({
                    url: `users/${env.userId}/profile_avatar`,
                    method: "POST",
                    data: helpers.serializeFormData({
                        avatar: helpers.asFileUpload(values.avatar),
                    }),
                })
            }

            await this.fetch_data();
        } catch (err) {
            AlertManager.default.push(<>
                <b>Error:</b> An error occurred and your changes could not be saved.
            </>, { level: 'error' })
            return transformFormErrors(err, form);
        }

        AlertManager.default.push(<>
            <b>Saved:</b> Your profile has been updated.
        </>, { level: 'success' })
    }

    renderForm(props: FormRenderProps) {
        return <Flex direction="row" justifyItems="center">
            <Flex.Item size="1000px" shouldShrink>
                <View>
                    <Heading level="h3" color="primary">
                        <Text size="x-small" transform="uppercase">Profile / Settings PII</Text>
                    </Heading>
                </View>

                <View as="div" padding="large 0">
                    <Field name="avatar" component={AvatarEditHeader}
                        username={props.values.full_name}
                    />
                </View>

                <div style={{ display: "flex", gap: "80px", flexDirection: "row", alignItems: "start" }}>
                    <Flex.Item shouldGrow>
                        <SectionHeader title="User Profile" />

                        <View>
                            <View as="div" margin="0 0 small">
                                <Field name="full_name" component={TextInput}
                                    renderLabel="Full Name"
                                    messages={[{ type: "hint", text: "This name will be used for grading." }]}
                                />
                            </View>

                            <View as="div" margin="0 0 small">
                                <Field name="display_name" component={TextInput}
                                    renderLabel="Display Name"
                                    messages={[{ type: "hint", text: "People will see this name in discussions, messages and comments." }]}
                                />
                            </View>

                            <View as="div" margin="0 0 small">
                                <Field name="sortable_name" component={TextInput}
                                    renderLabel="Sortable Name"
                                    messages={[{ type: "hint", text: "This name appears in sorted lists." }]}
                                />
                            </View>

                            {/* <View as="div" margin="0 0 small">
                                <Field name="bio" component={TextArea}
                                    label="Biography"
                                />
                            </View> */}

                            {/* <View as="div" margin="0 0 small">
                                <Field name="links" component={EditableList}
                                    title="Links"
                                    renderItem={render_link_item}
                                    renderEdit={render_link_edit}
                                />
                            </View> */}
                        </View>

                    </Flex.Item>
                    <Flex.Item size="350px">
                        <SectionHeader title="Contact" />

                        <View as="div" margin="0 0 small">
                            <Field name="emails" component={EditableList}
                                title="Email"
                                writeOnce
                                minimum={1}
                                renderItem={render_contact_item}
                                renderEdit={render_email_edit}
                            />
                        </View>

                        <View as="div" margin="0 0 small">
                            <Field name="sms_numbers" component={EditableList}
                                title="Text (SMS)"
                                writeOnce
                                renderItem={render_contact_item}
                                renderEdit={render_number_edit}
                            />
                        </View>

                        <View as="div" margin="large 0 small">
                            <Field name="password" component={TextInput}
                                type="password"
                                placeholder="Leave blank for unchanged"
                                renderLabel="Update Password"
                                validate={password_validator}
                                messages={[
                                    { type: "hint", text: "You will need to log in again after changing your password." }
                                ]}
                            />
                        </View>

                        {props.values.password && <View as="div" margin="0 0 small">
                            <Field name="confirm_password" component={TextInput}
                                type="password"
                                renderLabel="Confirm Password"
                                validate={confirm_password_validator}
                            />
                        </View>}
                    </Flex.Item>
                </div>
                <View as="div" margin="large 0 0" padding="small" borderWidth="small 0 0 0" textAlign="end">
                    <FormSubmitButton renderIcon={<IconSaveLine />} />
                </View>
            </Flex.Item>
        </Flex>
    }

    render() {
        return <div className="App" style={{ height: "min-content" }}>
            <PositionedWaterMark />
            <LtiFitHelper mode="expand" />
            <IasAppSpinner show={!this.loaded && !this.data} />
            {this.loaded && !this.data && <DeclAlert renderCloseButtonLabel={null} variant="error">Your account does not support editing your profile</DeclAlert>}
            {this.data && <Form onSubmit={this.saveProfile} render={this.renderForm} initialValues={this.savedState} />}
        </div>
    }
}
